import { jwtDecode } from "jwt-decode";
import { UserProfileDetails, UserRole } from "../types";
import devLogger from "./devLogger";

export const isValidEmail = (email: string) =>
  /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email);

export const isFormValid = (
  loginDetails: { email: string | undefined; password: string | undefined },
  errors: { email?: string; password?: string }
) =>
  !!loginDetails.email &&
  !!loginDetails.password &&
  !errors.email &&
  !errors.password;

const api_headers: {
  Accept: string;
  "Content-Type"?: string;
  Authorization?: string;
} = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

export const getHeaders = (options?: { omitContentType?: boolean }) => {
  const headers = { ...api_headers };

  // Remove Content-Type if specified (useful for FormData)
  if (options?.omitContentType) {
    delete headers["Content-Type"];
  }

  return headers;
};

export const getTokenizedHeaders = () => {
  const headers = { ...api_headers };

  const jwt = localStorage.getItem("token");
  if (jwt) headers.Authorization = `Bearer ${jwt}`;

  return headers;
};

export const isAdminRoute = () => {
  const pathname = window.location.pathname;
  return pathname.includes("/admin");
};

/**
 * Gets the current user details from localStorage
 * @returns The current UserProfileDetails object or null if none exists
 */
export const getUserDetailsCache = (): UserProfileDetails | null => {
  const stored = localStorage.getItem("details");
  return stored ? JSON.parse(stored) : null;
};

/**
 * Updates specific fields in the user details cache stored in localStorage
 * @param updates Partial object containing only the fields to update
 * @returns The updated UserProfileDetails object or null if no details exist
 */
export const updateUserDetailsCache = (
  updates: Partial<UserProfileDetails>
): UserProfileDetails | null => {
  const currentDetails = getUserDetailsCache();

  if (!currentDetails) return null;

  try {
    // Create updated details by merging current details with updates
    const updatedDetails: UserProfileDetails = {
      ...currentDetails,
      ...updates,
    };

    // Save the updated details back to localStorage
    localStorage.setItem("details", JSON.stringify(updatedDetails));

    return updatedDetails;
  } catch (error) {
    devLogger.error("Error updating user details cache:", error);
    return null;
  }
};

/**
 * Checks if the user's email is verified by accessing the latest data from localStorage
 * @returns Boolean indicating if email is verified
 */
export const isEmailVerified = (): boolean => {
  const userDetails = getUserDetailsCache();
  return userDetails?.isEmailVerified || false;
};

export const isTokenValid = (token: string | null): boolean => {
  if (!token) return false;

  try {
    const decoded: { exp: number } = jwtDecode(token);
    return decoded.exp * 1000 > Date.now();
  } catch (error) {
    // If token decoding fails, consider it invalid
    return false;
  }
};

export const isLoggedIn = (): boolean => {
  const token = localStorage.getItem("token");
  return !!token && isTokenValid(token);
};

/**
 * Gets the user's role from the latest user details in localStorage
 * @returns The user's role or undefined if not found
 */
export const getUserRole = (): UserRole => {
  const token = localStorage.getItem("token");
  const userDetails = getUserDetailsCache();

  if (!token || !userDetails) {
    return undefined;
  }

  return userDetails.role;
};
